/**
 * SolutionImplementationPlan — "Miten aloitamme?" compact card with expandable timeline.
 *
 * Surface: firstStep (DO NOW) + lead orgs + rationale
 * Expand: 12-month timeline phases with org actions + risks
 *
 * Data: workshop_solutions.ai_analysis.implementationPlan (JSONB)
 */
import { useState } from 'react';
import styled from '@emotion/styled';
import { ChevronDown, ChevronUp, Calendar, Building2, ArrowRight, Zap, Clock, Brain } from 'lucide-react';
import { sanitizeAiObject } from '../../utils/sanitizeAiText';
import EditableText from './EditableText';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const PlanSection = styled.div`
  margin-top: 28px;
  padding-top: 20px;
  border-top: 2px solid #f5f5f5;
`;

const PlanHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
`;

const PlanTitle = styled.span`
  font-size: 20px;
  font-weight: 700;
  color: #000;
`;

const AiPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 2px;
  color: #6d28d9;
  background: #f3e8ff;
  border: 1px solid #e9d5ff;
  flex-shrink: 0;
`;

const PlanDisclaimer = styled.div`
  font-size: 12px;
  color: #9ca3af;
  font-style: italic;
  margin: -4px 0 14px;
`;

const FirstStepBlock = styled.div`
  background: #f5f5f5;
  border: 1px solid #bfdbfe;
  border-radius: 0;
  padding: 14px 16px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const FirstStepLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #367ABA;
  margin-bottom: 4px;
`;

const FirstStepText = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #2c5e91;
  line-height: 1.5;
`;

const Rationale = styled.p`
  font-size: 13px;
  line-height: 1.6;
  color: #6b7280;
  margin: 10px 0 0;
  font-style: italic;
`;

const OrgBadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
`;

const OrgBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 2px;
  font-size: 12px;
  font-weight: 500;
  background: ${p => p.$role === 'lead' ? '#f5f5f5' : '#f3f4f6'};
  color: ${p => p.$role === 'lead' ? '#2c5e91' : '#4b5563'};
  border: 1px solid ${p => p.$role === 'lead' ? '#93c5fd' : '#e5e7eb'};
`;

const OrgRoleBadge = styled.span`
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${p => p.$role === 'lead' ? '#367ABA' : '#9ca3af'};
`;

const ExpandButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 14px;
  padding: 8px 14px;
  background: #fafafa;
  border: 1px solid #e2e8f0;
  border-radius: 2px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  width: 100%;

  &:hover { background: #f0f0f0; }
  @media print { display: none; }
`;

const TimelineWrap = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
`;

const TimelineLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
`;

const TimelineContainer = styled.div`
  display: flex;
  flex-direction: column;
  border-left: 3px solid #f5f5f5;
  margin-left: 8px;
`;

const TimelinePhase = styled.div`
  padding: 10px 0 10px 20px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: -7px;
    top: 16px;
    width: 11px;
    height: 11px;
    border-radius: 50%;
    background: ${p => p.$active ? '#367ABA' : '#93c5fd'};
    border: 2px solid #fff;
  }
`;

const PhaseLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #367ABA;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
`;

const PhaseTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #000;
  margin-bottom: 6px;
`;

const ActionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ActionItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  line-height: 1.5;
  color: #4b5563;
`;

const ActionWho = styled.span`
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
  flex-shrink: 0;
`;

const RiskCard = styled.div`
  margin-top: 14px;
  padding: 12px 14px;
  background: rgba(232, 17, 75, 0.08);
  border: 1px solid #fecaca;
  border-radius: 2px;
  font-size: 13px;
  line-height: 1.6;
  color: #a83d10;
`;

const RiskLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #fa6129;
  margin-bottom: 4px;
`;

const VisionCard = styled.div`
  margin-top: 14px;
  padding: 12px 14px;
  border: 1px solid ${p => p.$border || '#e9d5ff'};
  background: ${p => p.$bg || '#faf5ff'};
  border-radius: 2px;
  font-size: 13px;
  line-height: 1.6;
  color: ${p => p.$color || '#6b21a8'};
`;

const VisionLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${p => p.$color || '#9333ea'};
  margin-bottom: 4px;
`;

// Given a block of text and a list of organisation names, return a React
// fragment where every org name occurrence is wrapped in <strong>.
// Longest-first matching avoids partial shadowing ("Helsingin kaupunki" wins
// over "Helsinki"). Duplicates are OK — React re-renders are cheap here.
function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
function renderWithOrgsBold(text, orgNames) {
  if (!text || !orgNames?.length) return text;
  const cleaned = orgNames
    .map(n => (n || '').trim())
    .filter(n => n.length >= 3)
    .sort((a, b) => b.length - a.length);
  if (!cleaned.length) return text;
  const pattern = new RegExp('(' + cleaned.map(escapeRegex).join('|') + ')', 'g');
  const parts = text.split(pattern);
  return parts.map((part, i) =>
    cleaned.includes(part) ? <strong key={i}>{part}</strong> : part
  );
}

// Normalize AI-generated plan shape. Two variants in the wild:
//   Current schema: timeline[{ phase, title, actions:[{who, what, deliverable}] }]
//   Legacy drift:   phases[{ label, actions:[string] }]
function normalizeTimeline(plan) {
  if (!plan) return [];

  // Map a single phase entry to { phase, title, actions } shape, accepting
  // English or Finnish field names. Newer Sonnet-generated plans use
  // {vaihe, toimenpiteet}; older plans used {phase, actions} or {label,
  // actions}; legacy drift wraps actions as plain strings.
  const normaliseEntry = (p) => ({
    phase: p.phase || p.vaihe || p.label || '',
    title: p.title || '',
    actions: (p.actions || p.toimenpiteet || []).map(a =>
      typeof a === 'string' ? { what: a } : a
    ),
  });

  if (plan.timeline?.length) return plan.timeline.map(normaliseEntry);
  if (plan.phases?.length) return plan.phases.map(normaliseEntry);
  return [];
}

export default function SolutionImplementationPlan({ plan: rawPlan, initialExpanded = false, solutionId = null }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(initialExpanded);

  const plan = sanitizeAiObject(rawPlan);
  const timeline = normalizeTimeline(plan);
  if (!timeline.length) return null;

  const lastPhase = timeline[timeline.length - 1];
  const yearOutcome = lastPhase?.title;
  const summaryText = plan.summary && !plan.rationale ? plan.summary : null;

  // firstStep is a string, but newer Sonnet generations sometimes return
  // { kuvaus, vastuutaho, aikajana } — rendering that object crashed the
  // report's print view (#1007). Flatten it to one line.
  const firstStep = plan.firstStep && typeof plan.firstStep === 'object'
    ? [
        plan.firstStep.kuvaus || plan.firstStep.description,
        plan.firstStep.vastuutaho || plan.firstStep.who,
        plan.firstStep.aikajana || plan.firstStep.when,
      ].filter(v => typeof v === 'string' && v.trim()).join(' — ')
    : plan.firstStep;

  // Normalise orgs from either leadOrganisations (structured) or keyOrgs (flat strings).
  // Strings often come as "Name — description of role"; keep only the name before "—".
  // Object keys may arrive in Finnish ({nimi, rooli}) from newer Sonnet generations
  // — normalise to the {orgName, role, why} shape the renderer expects.
  const rawOrgs = plan.leadOrganisations?.length
    ? plan.leadOrganisations.map(o => ({
        orgName: o.orgName || o.nimi || o.name || '',
        role:    o.role    || o.rooli || '',
        why:     o.why     || o.miksi || '',
      }))
    : (plan.keyOrgs || []).map(o =>
        typeof o === 'string'
          ? { orgName: o.split(/[—–]/)[0].trim(), role: undefined, why: o }
          : o
      );

  // Role badge sanity. The badge is for SHORT labels ("lead", "support",
  // "advisor") — showing it needs (a) real differentiation (≥2 distinct
  // roles, not all the same) AND (b) the roles to actually be label-like.
  // Newer AI generations emit full-sentence role DESCRIPTIONS; those must
  // NOT go in a chip (it explodes the row and buries the org name). When
  // roles are long, we drop the badge and surface the role on hover instead.
  const roleValues = rawOrgs.map(o => o.role).filter(Boolean);
  const roles = new Set(roleValues);
  const rolesAreLabels = roleValues.length > 0 && roleValues.every(r => r.trim().length <= 24);
  const showRoleBadges = roles.size >= 2 && rolesAreLabels;

  // Cap visible orgs at 6 with a "+N more" chip to avoid wall-of-LEAD effect.
  const ORG_LIMIT = 6;
  const keyOrgs = rawOrgs.slice(0, ORG_LIMIT);
  const hiddenOrgCount = rawOrgs.length - keyOrgs.length;

  // List of org names used to bold mentions inside action-item prose.
  const allOrgNames = rawOrgs.map(o => o.orgName).filter(Boolean);

  return (
    <PlanSection>
      <PlanHeader>
        <PlanTitle>{t('report.implementationPlan.title')}</PlanTitle>
        <AiPill title={t('report.implementationPlan.aiDisclaimer')}>
          <Brain size={12} />
          {t('report.implementationPlan.aiDisclaimer')}
        </AiPill>
      </PlanHeader>
      <PlanDisclaimer>
        {t('report.implementationPlan.disclaimer')}
      </PlanDisclaimer>

      {/* Surface: DO NOW + rationale + orgs */}
      {firstStep && (
        <FirstStepBlock>
          <Zap size={18} style={{ color: '#367ABA', flexShrink: 0, marginTop: 1 }} />
          <div>
            <FirstStepLabel>{t('report.implementationPlan.firstStep')}</FirstStepLabel>
            <FirstStepText>
              {solutionId
                ? <EditableText fieldKey={`solution.${solutionId}.plan.firstStep`} fallback={firstStep} />
                : firstStep}
            </FirstStepText>
          </div>
        </FirstStepBlock>
      )}

      {(plan.rationale || summaryText) && (
        <Rationale>
          {solutionId
            ? <EditableText fieldKey={`solution.${solutionId}.plan.rationale`} fallback={plan.rationale || summaryText} />
            : (plan.rationale || summaryText)}
        </Rationale>
      )}

      {keyOrgs.length > 0 && (
        <OrgBadgeRow>
          {keyOrgs.map((org, i) => (
            <OrgBadge key={i} $role={org.role} title={[org.role, org.why].filter(Boolean).join(' — ') || undefined}>
              <Building2 size={12} />
              {showRoleBadges && org.role && (
                <OrgRoleBadge $role={org.role}>{org.role}</OrgRoleBadge>
              )}
              {org.orgName}
            </OrgBadge>
          ))}
          {hiddenOrgCount > 0 && (
            <OrgBadge title={t('report.implementationPlan.moreOrgsTitle', { count: hiddenOrgCount })}>
              {t('report.implementationPlan.moreOrgsShort', { count: hiddenOrgCount })}
            </OrgBadge>
          )}
        </OrgBadgeRow>
      )}

      {/* Expand: year timeline */}
      <ExpandButton onClick={() => setExpanded(!expanded)}>
        <Clock size={14} />
        <span style={{ flex: 1, textAlign: 'left' }}>
          {t('report.implementationPlan.timelineExpand')}{yearOutcome ? ` — ${yearOutcome}` : ''}
        </span>
        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </ExpandButton>

      {expanded && (
        <TimelineWrap>
          <TimelineLabel>
            <Calendar size={13} /> {t('report.implementationPlan.yearAchievable')}
          </TimelineLabel>

          <TimelineContainer>
            {timeline.map((phase, i) => (
              <TimelinePhase key={i} $active={i === 0}>
                <PhaseLabel>
                  {solutionId
                    ? <EditableText fieldKey={`solution.${solutionId}.plan.timeline.${i}.phase`} fallback={phase.phase} multiline={false} />
                    : phase.phase}
                </PhaseLabel>
                {phase.title && (
                  <PhaseTitle>
                    {solutionId
                      ? <EditableText fieldKey={`solution.${solutionId}.plan.timeline.${i}.title`} fallback={phase.title} multiline={false} />
                      : phase.title}
                  </PhaseTitle>
                )}
                <ActionList>
                  {phase.actions?.map((action, j) => {
                    const actionText = [
                      action.who && `${action.who}:`,
                      action.what,
                      action.deliverable && `→ ${action.deliverable}`,
                    ].filter(Boolean).join(' ');
                    return (
                    <ActionItem key={j}>
                      <ArrowRight size={12} style={{ marginTop: 4, flexShrink: 0, color: '#93c5fd' }} />
                      <div>
                        {solutionId ? (
                          <EditableText
                            fieldKey={`solution.${solutionId}.plan.timeline.${i}.actions.${j}`}
                            fallback={actionText}
                          />
                        ) : (
                          <>
                            {action.who && <><ActionWho>{action.who}:</ActionWho>{' '}</>}
                            {renderWithOrgsBold(action.what, allOrgNames)}
                            {action.deliverable && (
                              <span style={{ color: '#9ca3af' }}> → {renderWithOrgsBold(action.deliverable, allOrgNames)}</span>
                            )}
                          </>
                        )}
                      </div>
                    </ActionItem>
                    );
                  })}
                </ActionList>
              </TimelinePhase>
            ))}
          </TimelineContainer>

          {(() => {
            const lastDeliverables = (lastPhase?.actions || [])
              .map(a => a?.deliverable)
              .filter(Boolean);
            const fallbackYear = lastDeliverables.length
              ? lastDeliverables.join('. ') + '.'
              : (plan.summary || yearOutcome || null);
            const yearText = plan.yearVision || fallbackYear;
            if (!yearText) return null;
            return (
              <VisionCard>
                <VisionLabel>{t('report.implementationPlan.yearAchieved')}</VisionLabel>
                {solutionId
                  ? <EditableText fieldKey={`solution.${solutionId}.plan.yearVision`} fallback={yearText} />
                  : yearText}
              </VisionCard>
            );
          })()}

          {(() => {
            // Always render a govt-term card. When explicit text is missing
            // (some plans generated before the schema included it), derive a
            // plausible 4-year horizon from the summary + 12-month outcome.
            const explicit = plan.governmentTermVision;
            const derived = !explicit && plan.summary
              ? t('report.implementationPlan.govtTermDerived', { summary: plan.summary })
              : null;
            const text = explicit || derived;
            if (!text) return null;
            return (
              <VisionCard $bg="#f5f5f5" $border="#bfdbfe" $color="#2c5e91">
                <VisionLabel $color="#367ABA">{t('report.implementationPlan.govtTermAchievable')}</VisionLabel>
                {explicit && solutionId
                  ? <EditableText fieldKey={`solution.${solutionId}.plan.governmentTermVision`} fallback={explicit} />
                  : text}
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 8, fontStyle: 'italic' }}>
                  {explicit
                    ? t('report.implementationPlan.aiAccelerationNote')
                    : t('report.implementationPlan.govtTermPlaceholder')}
                </div>
              </VisionCard>
            );
          })()}

          {plan.risks?.length > 0 && (
            <RiskCard>
              <RiskLabel>{t('report.implementationPlan.risks')}</RiskLabel>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {plan.risks.map((r, i) => {
                  // Newer plan generations return structured risk objects
                  // {riski, kuvaus, lieventäminen}; older plans returned
                  // plain strings. Handle both.
                  if (typeof r === 'string') {
                    return (
                      <li key={i}>
                        {solutionId
                          ? <EditableText fieldKey={`solution.${solutionId}.plan.risks.${i}`} fallback={r} />
                          : r}
                      </li>
                    );
                  }
                  if (r && typeof r === 'object') {
                    return (
                      <li key={i} style={{ marginBottom: 8 }}>
                        <strong>
                          {solutionId
                            ? <EditableText fieldKey={`solution.${solutionId}.plan.risks.${i}.riski`} fallback={r.riski || r.risk || ''} multiline={false} />
                            : (r.riski || r.risk)}
                        </strong>
                        {(r.kuvaus || r.description) && (
                          <div style={{ marginTop: 2 }}>
                            {solutionId
                              ? <EditableText fieldKey={`solution.${solutionId}.plan.risks.${i}.kuvaus`} fallback={r.kuvaus || r.description} />
                              : (r.kuvaus || r.description)}
                          </div>
                        )}
                        {(r.lieventaminen || r['lieventäminen'] || r.mitigation) && (
                          <div style={{ marginTop: 4, fontStyle: 'italic', opacity: 0.85 }}>
                            <strong>{t('report.implementationPlan.mitigation')}</strong>{' '}
                            {solutionId
                              ? <EditableText fieldKey={`solution.${solutionId}.plan.risks.${i}.lieventaminen`} fallback={r.lieventaminen || r['lieventäminen'] || r.mitigation} />
                              : (r.lieventaminen || r['lieventäminen'] || r.mitigation)}
                          </div>
                        )}
                      </li>
                    );
                  }
                  return null;
                })}
              </ul>
            </RiskCard>
          )}
        </TimelineWrap>
      )}
    </PlanSection>
  );
}
